import { array } from 'yup';
import { BaseApiResponseWithPagination } from '../../types/shared.types';
import CryptoJS from 'crypto-js';
import { SxProps } from '@mui/material';
// enum for table
enum Status {
  Active = 'ACTIVE',
  Completed = 'COMPLETED',
  Open = 'OPEN',
  Expired = 'EXPIRED'
}

enum BudgetType {
  HOURLY = 'HOURLY',
  FIXED = 'FIXED'
}

interface BudgetParams {
  budget_type: BudgetType;
  minimum_hourly_rate: number | null;
  maximum_hourly_rate: number;
  maximum_budget: number;
}

const PROXY_URL = process.env.REACT_APP_PROXY_URL
  ? process.env.REACT_APP_PROXY_URL
  : 'https://devassets.hirecoder.info/';

const getPostTime = (value: string): any => {
  let time = '';
  const targetTime: any = new Date(value);
  const currentTime: any = new Date();
  const timeDifference = currentTime - targetTime;
  const hoursDifference = timeDifference / (1000 * 60 * 60);

  if (hoursDifference <= 24) {
    time = `${Math.round(hoursDifference)} ${hoursDifference > 1 ? 'hours' : 'hour'}`;
  } else if (hoursDifference <= 24 * 7) {
    const daysDifference = Math.floor(hoursDifference / 24);
    time = `${Math.round(daysDifference)} ${daysDifference > 1 ? 'days' : 'day'}`;
  } else if (hoursDifference <= 24 * 7 * 4) {
    const weeksDifference = Math.floor(hoursDifference / (24 * 7));
    time = `${Math.round(weeksDifference)} ${weeksDifference > 1 ? 'weeks' : 'week'}`;
  } else {
    const monthsDifference = Math.floor(hoursDifference / (24 * 7 * 4));
    time = `${Math.round(monthsDifference)} ${monthsDifference > 1 ? 'months' : 'month'}`;
  }

  return time;
};

const urlToFile = async (imageUrls: any) => {
  try {
    if (Array.isArray(imageUrls) && imageUrls.length > 0) {
      const responses = await Promise.all(
        imageUrls.map(async (url: string) => {
          const response = await fetch(url);
          const blob = await response.blob();
          const filename = url.substring(url.lastIndexOf('/') + 1);
          return new File([blob], filename, { type: blob.type });
        })
      );
      return responses;
    } else if (typeof imageUrls === 'string') {
      const response = await fetch(imageUrls);
      const blob = await response.blob();
      const filename = imageUrls.substring(imageUrls.lastIndexOf('/') + 1);
      return new File([blob], filename, { type: blob.type });
    } else {
      throw new Error('Invalid input: imageUrls must be a non-empty array or a string.');
    }
  } catch (error) {
    console.error('Error converting URL to File:', error);
    throw error;
  }
};

const parseCustomDate = (dateString: string): Date | null => {
  const [month, day, year] = dateString.split('-').map(Number);
  if (!month || !day || !year) return null;
  return new Date(year, month - 1, day); // Months are zero-indexed
};

const formatDate = (date: any) => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const successStatusCodeList: number[] = [200, 201];

function generatePageOptions(count: number, totalPages: number) {
  const options = [];
  const itemsPerPage = Math.ceil(count / totalPages);
  for (let i = 1; i <= totalPages; i++) {
    const label = i * itemsPerPage;
    options.push({ label, value: label });
  }
  return options;
}

function generatePreviousCount(
  currentPageNumber: number,
  currentPageCount: number,
  pageCount: number
) {
  let count = 0;
  if (currentPageNumber > 1) {
    count = (currentPageNumber - 1) * pageCount + currentPageCount;
  } else {
    count = currentPageCount;
  }
  return count;
}

function createHttpParams(options: { [param: string]: string }): string {
  return '?' + new URLSearchParams(options).toString();
}

function getExpertiseLabel(beginner: boolean, intermediate: boolean, expert: boolean) {
  const labels = [];

  if (beginner) {
    labels.push('Beginner');
  }

  if (intermediate) {
    labels.push('Intermediate');
  }

  if (expert) {
    labels.push('Expert');
  }

  return labels.join(', ') || '';
}

const projectDuration: any = {
  SHORT_TERM: '1 to 3 months',
  MEDIUM_TERM: '3 to 6 months',
  LONG_TERM: 'More than 6 months'
};

// status function for table
const getStatusLabelAndStyle = (status: any) => {
  let label = '';
  let styleChip = '';
  switch (status) {
    case Status.Active:
      label = 'Active';
      styleChip = 'chip--active';
      break;
    case Status.Completed:
      label = 'Completed';
      styleChip = 'chip--completed';
      break;
    case Status.Open:
      label = 'Open';
      styleChip = 'chip--open';
      break;
    case Status.Expired:
      label = 'Expired';
      styleChip = 'chip--expired';
      break;
    default:
      styleChip = '';
  }
  return { label, styleChip };
};

// handle budget Utils functions for table
const formatBudget = (row: BudgetParams): string => {
  const { budget_type, minimum_hourly_rate, maximum_hourly_rate, maximum_budget } = row;

  if (budget_type === BudgetType.HOURLY) {
    if (minimum_hourly_rate !== null) {
      return `${minimum_hourly_rate} - ${maximum_hourly_rate}/hr`;
    } else {
      return `${maximum_hourly_rate}/hr`;
    }
  } else {
    return `${maximum_budget}`;
  }
};
function isObjectEmpty(obj: any) {
  return obj && obj.constructor === Object && Object.keys(obj).length === 0;
}

function isEmptyArray(a: any[]) {
  return !a || (Array.isArray(a) && !a.length);
}

function mapEntity(
  items: Array<any>,
  matchValue: string,
  propToMatch: string,
  propToReturn?: string
): string | null {
  if (!items.length || !propToMatch) {
    return null;
  }

  return items.find((item) => {
    return item[propToMatch] === matchValue;
  })?.[propToReturn ?? propToMatch];
}

const handleDebounce = (callback: any, debounceTime: number) => {
  let typingTimeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(typingTimeout);
    typingTimeout = setTimeout(() => {
      callback(...args);
    }, debounceTime);
  };
};

const getErrorMszOnFailure = (data: any) => {
  if (typeof data === 'string') {
    return 'Oops, something went wrong!';
  } else {
    for (const key in data) {
      return typeof data === 'object' ? data[key] : data[key][0];
    }
  }
};

function cleanObjectProps(object: Record<string, string>): Record<string, string> {
  let cleanObj = {};

  Object.keys(object).forEach((key) => {
    const property = object[key];
    cleanObj = property ? { ...cleanObj, [key]: property } : cleanObj;
  });

  return cleanObj;
}

export const convertToCSV = (objArray: string) => {
  const array = typeof objArray != 'object' ? JSON.parse(objArray) : objArray;
  let str = '';

  for (let i = 0; i < array.length; i++) {
    let line = '';
    for (const index in array[i]) {
      if (line !== '') line += ',';

      line += array[i][index];
    }
    str += line + '\r\n';
  }
  return str;
};

export const exportCSVFile = (items: any[], fileTitle: string, headers: object) => {
  try {
    if (headers) {
      items.unshift(headers);
    }
    // Convert Object to JSON
    const jsonObject = JSON.stringify(items);
    const csv = convertToCSV(jsonObject);
    const exportedFilenmae = fileTitle + '.csv' || 'export.csv';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportedFilenmae);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (e: any) {
    throw 'Unable to download CSV';
  }
};
const getLocalTime = (country: string, countryTimeOffsets: Record<string, number>) => {
  // Check if the provided country has a corresponding time offset
  if (countryTimeOffsets.hasOwnProperty(country)) {
    const offsetHours = countryTimeOffsets[country];
    const currentTime = new Date();
    const localTime = new Date(currentTime.getTime() + offsetHours * 60 * 60 * 1000);
    const hours = localTime.getHours();
    const minutes = localTime.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
    const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
    return `${formattedHours}:${formattedMinutes} ${ampm}`;
  } else {
    return 'NA';
  }
};

const getApprovedTechnologies = (technologies: any) => {
  const tempTech: any = [];
  technologies.forEach((tech: any) => {
    if (tech.is_approved) {
      tempTech.push(tech);
    }
  });
  return tempTech;
};

const checkStatusType = (data: any[], status: string, key: string, type: string): boolean => {
  let statusCount = 0;
  if (data.length > 0) {
    data.forEach((item: any) => {
      if (key && item[key][status] === type) {
        statusCount++;
      } else if (item[status] === type) {
        statusCount++;
      }
    });
  } else {
    return false;
  }
  return data.length === statusCount;
};
const getContactPayload = (data: any, country: any, state: any) => {
  const payload = {
    subject: '',
    body: '',
    from_email: 'no-reply@hirecoder.info'
  };
  payload.subject = data.subject;
  payload.body = `
    Hi Admin,

    Request from: ${data.firstName} ${data.lastName}
    Address: ${
      country?.value && state?.value && data.address && data.city && data.zipcode
        ? `${data.address}, ${data.city}, ${state?.value} ${data.zipcode}, ${country?.value}`
        : null
    }
    User email: ${data.email}

    Message: ${data.message}
  `;
  return payload;
};

function calculateTotalHours(startTime: any, endTime: any) {
  // Function to convert time string to minutes
  function timeToMinutes(time: any) {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // Convert time strings to minutes
  const startTimeMinutes = timeToMinutes(startTime);
  const endTimeMinutes = timeToMinutes(endTime);

  // Calculate the total hours
  const totalMinutes = endTimeMinutes - startTimeMinutes;
  const totalHours = totalMinutes / 60;

  return totalHours;
}
// Function to generate a random alphanumeric ID of length n
const generateRandomId = (n: any) => {
  const chars = process.env.REACT_APP_CRYPTO_JS_CHAR_KEY || '';
  let randomId = '';
  for (let i = 0; i < n; i++) {
    randomId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return randomId;
};
const encryptedUrlData = (data: any) => {
  const encryptData = CryptoJS.AES.encrypt(
    JSON.stringify(data),
    process.env.REACT_APP_CRYPTO_JS_SECRET_KEY as string
  ).toString();
  const id = generateRandomId(process.env.REACT_APP_CRYPTO_JS_KEY_NUMBER);
  sessionStorage.setItem(id, encryptData);
  return id;
};

const decryptedUrlData = (data: any) => {
  const encrypted = sessionStorage.getItem(data) ?? '';
  const bytes = CryptoJS.AES.decrypt(
    encrypted,
    process.env.REACT_APP_CRYPTO_JS_SECRET_KEY as string
  );
  const decryptData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  return decryptData;
};

const encryptedData = (data: any) => {
  const encryptData = CryptoJS.AES.encrypt(
    JSON.stringify(data),
    process.env.REACT_APP_CRYPTO_JS_SECRET_KEY as string
  ).toString();
  return encryptData;
};

const decryptedData = (encrypted: any) => {
  try {
    const bytes = CryptoJS.AES.decrypt(
      encrypted,
      process.env.REACT_APP_CRYPTO_JS_SECRET_KEY as string
    );
    const decryptData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    return decryptData;
  } catch (error) {
    console.warn(error);
  }
};
const encryptedPayloadData = (data: any, type: string) => {
  if (type === 'payload') {
    const key = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_JS_SECRET_KEY as string);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString();
  }

  if (type === 'bearer') {
    const cipher = CryptoJS.AES.encrypt(
      data,
      CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_JS_SECRET_KEY as string),
      {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7
      }
    );
    const encryptedData = cipher.toString();
    return encryptedData;
  }

  return null;
};

const decryptedResponseData = (encrypted: any) => {
  const key = CryptoJS.enc.Utf8.parse(process.env.REACT_APP_CRYPTO_JS_SECRET_KEY as string);
  const bytes = CryptoJS.AES.decrypt(encrypted, key, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7
  });
  const decrypted = bytes.toString(CryptoJS.enc.Utf8);
  return JSON.parse(decrypted);
};

const getNotificationPostTime = (value: string): string => {
  let time = '';
  const targetTime: Date = new Date(value);
  const currentTime: Date = new Date();
  const timeDifference = currentTime.getTime() - targetTime.getTime();
  const secondsDifference = Math.floor(timeDifference / 1000);
  const minutesDifference = Math.floor(secondsDifference / 60);
  const hoursDifference = Math.floor(minutesDifference / 60);
  const daysDifference = Math.floor(hoursDifference / 24);
  const weeksDifference = Math.floor(daysDifference / 7);
  const monthsDifference = Math.floor(daysDifference / 30); // Approximation for months

  if (secondsDifference < 60) {
    time = `${secondsDifference} s`;
  } else if (minutesDifference < 60) {
    time = `${minutesDifference} m`;
  } else if (hoursDifference < 24) {
    time = `${hoursDifference} h`;
  } else if (daysDifference < 7) {
    time = `${daysDifference} d`;
  } else if (weeksDifference < 4) {
    time = `${weeksDifference} w`;
  } else {
    time = `${monthsDifference} mo`;
  }

  return time;
};

const hideDataGridFocusOnCell: SxProps = {
  '.MuiDataGrid-cell:focus': {
    outline: 'none'
  },
  '.MuiDataGrid-cell:focus-within': {
    outline: 'none'
  },
  '.MuiDataGrid-columnHeader:focus': {
    outline: 'none'
  },
  '.MuiDataGrid-columnHeader:focus-within': {
    outline: 'none'
  },
  '.MuiDataGrid-cell--editing': {
    backgroundColor: 'transparent'
  },
  '.Mui-selected': {
    backgroundColor: 'transparent !important'
  }
};

function isValidJSON(data: string) {
  try {
    JSON.parse(data);
    return true;
  } catch (e) {
    return false;
  }
}

export {
  getPostTime,
  urlToFile,
  successStatusCodeList,
  generatePageOptions,
  getExpertiseLabel,
  projectDuration,
  formatDate,
  getStatusLabelAndStyle,
  BudgetType,
  formatBudget,
  isObjectEmpty,
  isEmptyArray,
  mapEntity,
  createHttpParams,
  handleDebounce,
  getErrorMszOnFailure,
  cleanObjectProps,
  getLocalTime,
  generatePreviousCount,
  getApprovedTechnologies,
  checkStatusType,
  getContactPayload,
  calculateTotalHours,
  encryptedUrlData,
  decryptedUrlData,
  encryptedData,
  decryptedData,
  encryptedPayloadData,
  decryptedResponseData,
  getNotificationPostTime,
  hideDataGridFocusOnCell,
  isValidJSON,
  parseCustomDate
};
